/**
 * Created by Ning Jiang on 2021-06-23.
 */

import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap, TimelineLite} from "gsap";
import {DeadwoodRip} from "../DeadwoodRip";
import {DeadwoodRipLayers, Layers} from "../view/DeadwoodRipLayers";
import {DeadwoodRipGameAssets} from "../DeadwoodRipGameAssets";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {IBetWayWinData} from "../server/data/IDeadwoodRipData";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {DeadwoodRipReelView} from "../reels/DeadwoodRipReelView";

export class WinHighlightEffect extends PIXI.Sprite {

    private _spine:PIXI.spine.Spine;

    constructor() {
        super();
        this._spine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("winHint"));
        this._spine.autoUpdate = true;
        this.addChild(this._spine);
    }

    public playHintEffect():TimelineLite {
        const tl = new TimelineLite();
        tl.add(() => {
            this._spine.state.setAnimation(0, "winHit", false);
        });
        tl.add(() => {}, this._spine.spineData.findAnimation("winHit").duration);
        return tl;
    }

    public playWinEffect():TimelineLite {
        const tl = new TimelineLite();
        tl.add(() => {
            this._spine.state.setAnimation(0, "win", false).timeScale = 0.5;
        });
        tl.add(() => {}, this._spine.spineData.findAnimation("win").duration * 2);
        return tl;
    }
}

export class IndividualWinText extends PIXI.Container {

    private _maxWayWidth:number = 140;

    constructor(winAmount:number, way:number) {
        super();
        // winAmount = 100000000;
        // way = 11216;
        const winNumberContainer = new PIXI.Container();
        this.addChild(winNumberContainer);
        winNumberContainer.y = -10

        let winTotalWidth = 0;
        const winNumber = new PIXI.BitmapText(`${SlotGame.apiPlugin.currency.formatValue(winAmount)}`, {
            fontName : DeadwoodRipGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize : 20,
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        winNumber.letterSpacing = -32;
        (<PIXI.Point>winNumber.anchor).set(0, 1.28);
        winNumberContainer.addChild(winNumber);
        winNumber.updateTransform();
        winTotalWidth += winNumber.textWidth;

        if(DeadwoodRip.multiplier.currentMultiplier > 1) {
            const multiplier = new PIXI.BitmapText(`x${DeadwoodRip.multiplier.currentMultiplier}`, {
                fontName : DeadwoodRipGameAssets.BITMAP_FONT_NUDGE_MULTIPLIER,
                fontSize : 16,
                tint : 0xffffff
            });
            (<PIXI.Point>multiplier.anchor).set(1, 1.36);
            multiplier.letterSpacing = -42;
            winNumberContainer.addChild(multiplier);
            multiplier.updateTransform();
            winTotalWidth += multiplier.textWidth + 4;
            multiplier.x = winTotalWidth * 0.5;
        }
        winNumber.x = -winTotalWidth * 0.5;

        if(winTotalWidth > this._maxWayWidth) {
            const winScale = MathHelper.roundToDecimals(this._maxWayWidth / winTotalWidth, 2);
            winNumberContainer.scale.set(winScale);
        }

        const waysContainer = new PIXI.Container();
        this.addChild(waysContainer);
        waysContainer.y = 32;

        const waysWonNumber = new PIXI.BitmapText(way.toString(), {
            fontName : DeadwoodRipGameAssets.BITMAP_FONT_WAYS_COUNT_UP,
            fontSize : 10,
            tint : 0xffffff
        });
        (<PIXI.Point>waysWonNumber.anchor).set(0, 1.5);
        waysWonNumber.letterSpacing = -34;
        waysContainer.addChild(waysWonNumber);

        const textureName:string = way === 1 ? 'totalways/way' : 'totalways/ways';
        const waysWonText = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures(textureName)[0]);
        waysWonText.anchor.set(1, 0.5);
        waysWonText.scale.set(0.76);
        waysContainer.addChild(waysWonText);

        waysWonNumber.updateTransform();
        const waysTotalWidth:number = waysWonNumber.textWidth + waysWonText.width;
        waysWonNumber.x = -waysTotalWidth / 2;
        waysWonText.x = waysTotalWidth / 2;
        if(waysTotalWidth > this._maxWayWidth) {
            const waysScale = MathHelper.roundToDecimals(this._maxWayWidth / waysTotalWidth, 2);
            waysContainer.scale.set(waysScale);
        }
    }
}

export class WinView {

    private _layer:StageLayer;
    private _individualWinText:IndividualWinText | null;
    private _individualWinTextTl:TweenLite | null;

    private _toggleStopped:boolean;

    constructor() {
        this._layer = DeadwoodRipLayers.getLayer(Layers.WIN_TEXTS)!;
    }

    public playInitialWinText(position:PIXI.Point, winData:IBetWayWinData):void {
        const winText = new IndividualWinText(MathHelper.roundToDecimals(winData.multipliedAmount / winData.multiplier, 2), winData.numberOfCombinations);
        winText.position.set(position.x, position.y);
        const winTextTl = new TimelineLite();
        winTextTl.add(() => {
            this._layer.addChild(winText);
        });
        winTextTl.add([
            gsap.to(winText, {y : position.y - 100, duration : 1}),
            gsap.to(winText, {alpha : 0, duration : 0.8, delay : 0.2})
        ], `+=0.3`);
        winTextTl.add(() => {
            this._layer.removeChild(winText);
            winText.destroy();
        });
    }

    public playIndividualWinText(position:PIXI.Point, winData:IBetWayWinData):void {
        if(this._toggleStopped) {
            return;
        }

        this.removeToggleWinText();
        if(this._toggleStopped) {
            return;
        }
        this._individualWinText = new IndividualWinText(MathHelper.roundToDecimals(winData.multipliedAmount / winData.multiplier, 2), winData.numberOfCombinations);
        this._individualWinText.position.set(position.x, position.y);
        this._individualWinText.alpha = 0;
        this._layer.addChild(this._individualWinText!);
        this._individualWinTextTl = gsap.to(this._individualWinText, {alpha : 1, duration : 0.1});
    }

    public startToggle():void {
        this._toggleStopped = false;
    }

    public stopToggle():void {
        this._toggleStopped = true;
        this.removeToggleWinText();
    }

    public removeToggleWinText():void {
        if(this._individualWinTextTl && this._individualWinTextTl.isActive()) {
            this._individualWinTextTl.progress(1);
        }
        this._individualWinTextTl = null;

        if(this._individualWinText) {
            if(this._individualWinText.parent) {
                this._individualWinText.parent.removeChild(this._individualWinText);
            }
            this._individualWinText.destroy();
            this._individualWinText = null;
        }
    }

    public getWinTextPosition(reelWinPositions:boolean[][]):PIXI.Point {
        for(let reelId = reelWinPositions.length - 1; reelId >= 0; reelId--) {
            if(DeadwoodRip.model.parsedGameData.evaluatedArea[2][0] === "WXN") {
                continue;
            }
            for(let symId = 0; symId < 4; symId++) {
                if(reelWinPositions[reelId][symId] && !DeadwoodRip.model.parsedGameData.reels[reelId][symId + 1].includes("W") && !DeadwoodRip.model.parsedGameData.reels[reelId][symId + 1].includes("S")) {
                    return new PIXI.Point(reelId, symId);
                }
            }
        }
        return new PIXI.Point(0, reelWinPositions[0].indexOf(true));
    }

    public getWinEffectAnimation(reelId:number, symId:number):TimelineLite {
        const effect = DeadwoodRip.winView.getWinEffect(DeadwoodRipReelView.getGlobalPosition(reelId, symId));
        const tl = new TimelineLite();
        tl.add(() => {
            DeadwoodRipLayers.getLayer(Layers.WIN_PRESENTATIONS)!.addChild(effect);
        });
        tl.add(effect.playWinEffect());
        tl.add(() => {
            DeadwoodRip.winView.removeWinEffect(effect);
        });
        return tl;
    }

    public getWinEffect(position:PIXI.Point):WinHighlightEffect {
        const effect = new WinHighlightEffect();
        effect.position.set(position.x, position.y);

        return effect;
    }

    public removeWinEffect(winEffect:WinHighlightEffect):void {
        if(winEffect.parent) {
            winEffect.parent.removeChild(winEffect);
        }
        winEffect.destroy();
    }
}